import {skipToken} from '@reduxjs/toolkit/query';
import {Button, DataStatus, Separator} from 'platform/components';
import {Heading, Hide, Show, Text, VStack} from 'platform/foundation';

import {useMemo} from 'react';
import {useParams} from 'react-router-dom';

import {isEmpty, isNotNil} from 'ramda';

import {suffixTestId, TestIdProps, useToggle} from 'shared/web';

import {useCatalogue} from '../../../hooks/useCatalogue';
import i18n from '../../../i18n/i18n';
import {useGetCarFeaturesListQuery} from '../../../store/priceReportApi';
import {AllFeaturesFiled} from './AllFeaturesFiled';
import {FeaturesField} from './FeaturesField';

export function VehicleFeatures(props: TestIdProps) {
  const {requestId} = useParams<{requestId?: string}>();
  const {hasFeatureTranslation} = useCatalogue();
  const [isOpen, toggleOpen] = useToggle();
  const {data, isLoading, isError} = useGetCarFeaturesListQuery(
    isNotNil(requestId) ? {id: requestId} : skipToken
  );
  const translatableFeatures = useMemo(
    () => data?.features?.filter(hasFeatureTranslation) ?? [],
    [data?.features, hasFeatureTranslation]
  );

  return (
    <VStack spacing={6} align="flex-start">
      <Heading size={2}>{i18n.t('page.create.labels.vehicleFeatures')}</Heading>
      <Text color="secondary">{i18n.t('page.create.labels.vehicleFeaturesDescription')}</Text>
      <DataStatus isLoading={isLoading} isError={isError}>
        <Heading size={4}>{i18n.t('page.create.labels.selectFeatures')}</Heading>
        <FeaturesField
          features={translatableFeatures}
          data-testid={suffixTestId('featuresField', props)}
        />
        <Hide when={isEmpty(translatableFeatures)}>
          <Button
            title={i18n.t('page.create.labels.showAllFeatures')}
            variant="ghostLink"
            rightIcon={isOpen ? 'navigation/expand_less' : 'navigation/expand_more'}
            onClick={toggleOpen}
            data-testid={suffixTestId('showAllFeatures', props)}
          />
        </Hide>
        <Show when={isOpen}>
          <Separator spacing={0} />
        </Show>
        <Show when={isOpen || isEmpty(translatableFeatures)}>
          <AllFeaturesFiled data-testid={suffixTestId('allFeaturesFiled', props)} />
        </Show>
      </DataStatus>
    </VStack>
  );
}
